import React, { useState, useEffect, useRef, useCallback } from 'react'
import logo from '../../assets/logo.png'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from '../../components/toast/CustomToast'
import api from '../../utils/api'
import NotFoundPage from '../NotFoundPage'
import MiddlewareStatusModal from '../../components/common/MiddlewareStatusModal'

// Import pages
import CompanyHomePage from './CompanyHomePage'
import CompanyIntegrationsPage from './CompanyIntegrationsPage'
import CompanyUsersPage from './CompanyUsersPage'
import CompanySettingsPage from './CompanySettingsPage'
import EventsTracePage from './EventsTracePage'
import IntegrationsErrorsPage from './IntegrationsErrorsPage'

// Import modals
import { EventsModal, ParametersModal, DocumentationModal } from '../../components/company/modals'
import CompanyConfigsPage from './CompanyConfigsPage'
import QRLoginModal from '../../components/company/QRLoginModal'
import FranchiseUsageCard from '../../components/company/FranchiseUsageCard'
import SessionCard from '../../components/common/SessionCard'
import FranchiseUsageAlert, { isFranchiseAlertDismissedToday } from '../../components/company/FranchiseUsageAlert'
import { franchiseService } from '../../services/franchise.service'

// ============ Token Utilities ============
const decodeToken = (token) => {
  try {
    if (!token) return null
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = JSON.parse(atob(payload))
    return decoded
  } catch (error) {
    console.error('Erro ao decodificar token:', error)
    return null
  }
}

const useTokenExpiration = (token, onExpire) => {
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [totalTokenTime, setTotalTokenTime] = useState(null)
  const [isExpiring, setIsExpiring] = useState(false)
  const totalTimeRef = useRef(null)
  const expirationTimeRef = useRef(null)

  useEffect(() => {
    if (!token) return

    const decoded = decodeToken(token)
    if (!decoded || !decoded.exp) return

    const expirationTime = decoded.exp * 1000
    expirationTimeRef.current = expirationTime

    if (!totalTimeRef.current) {
      const now = Date.now()
      const totalTime = expirationTime - now
      if (totalTime > 0) {
        totalTimeRef.current = totalTime
        setTotalTokenTime(totalTime)
      }
    } else {
      setTotalTokenTime(totalTimeRef.current)
    }

    const checkExpiration = () => {
      const now = Date.now()
      const remaining = expirationTimeRef.current - now
      const remainingMinutes = remaining / 60000

      setTimeRemaining(remaining)
      setIsExpiring(remainingMinutes <= 5)

      if (remaining <= 0) {
        if (onExpire) onExpire()
        return
      }
    }

    checkExpiration()
    const interval = setInterval(checkExpiration, 1000)

    return () => clearInterval(interval)
  }, [token, onExpire])

  return { timeRemaining, totalTokenTime, isExpiring }
}

// ============ NavItem Component ============
const NavItem = ({ icon, label, subtitle, active, onClick, collapsed }) => {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={`group relative w-full flex items-center rounded-lg transition-colors duration-150 short:py-1.5 ${
        collapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-3 py-2'
      } ${
        active
          ? 'bg-blue-50 dark:bg-blue-900/25 text-blue-600 dark:text-blue-400'
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-gray-200'
      }`}
    >
      {active && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-blue-500 dark:bg-blue-400" />
      )}
      <span className={`flex items-center justify-center rounded-md w-7 h-7 flex-shrink-0 transition-colors ${
        active ? 'bg-blue-100 dark:bg-blue-900/40' : 'group-hover:bg-gray-200/60 dark:group-hover:bg-gray-600/40'
      }`}>
        {icon}
      </span>
      {!collapsed && (
        <span className="min-w-0 text-left leading-tight">
          <span className="block font-medium text-[13px] truncate">{label}</span>
          {subtitle && (
            <span className="block text-[10.5px] text-gray-400 dark:text-gray-500 truncate short:hidden">{subtitle}</span>
          )}
        </span>
      )}

      {collapsed && (
        <span className="pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 text-white text-xs font-medium px-2.5 py-1.5 opacity-0 scale-95 origin-left transition-all duration-150 group-hover:opacity-100 group-hover:scale-100 z-50 shadow-lg">
          {label}
        </span>
      )}
    </button>
  )
}

// ============ Session Expiration Modal ============
const SessionExpirationModal = ({ isOpen, timeRemaining, onRenew, onLogout, loading }) => {
  if (!isOpen || !timeRemaining) return null

  const minutes = Math.max(0, Math.floor(timeRemaining / 60000))
  const seconds = Math.max(0, Math.floor((timeRemaining % 60000) / 1000))

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[9999] flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8 max-w-md w-full mx-4 border border-red-200 dark:border-red-800">
        <div className="flex items-start gap-3 mb-4">
          <span className="material-icons text-red-500 dark:text-red-400 text-2xl flex-shrink-0">warning</span>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sessão Expirando</h2>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Sua sessão expirará em{' '}
          <strong className="text-red-500 dark:text-red-400">
            {minutes}m {seconds}s
          </strong>
          . Para continuar trabalhando, renove sua sessão ou faça login novamente.
        </p>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
          <div className="flex gap-2">
            <span className="material-icons text-yellow-600 dark:text-yellow-400 flex-shrink-0" style={{ fontSize: '16px' }}>
              info
            </span>
            <p className="text-xs text-yellow-800 dark:text-yellow-300">
              <strong>Dica:</strong> Sua sessão será renovada com um novo token por mais 30 minutos.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onRenew}
            disabled={loading}
            className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition flex items-center justify-center gap-2"
          >
            <span className="material-icons sm">refresh</span>
            Renovar Sessão
          </button>
          <button
            onClick={onLogout}
            disabled={loading}
            className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-700 disabled:bg-gray-200 dark:disabled:bg-gray-700 text-gray-900 dark:text-white font-medium py-2 px-4 rounded-lg transition flex items-center justify-center gap-2"
          >
            <span className="material-icons sm">logout</span>
            Sair
          </button>
        </div>
      </div>
    </div>
  )
}

// ============ Session Expired Modal ============
const SessionExpiredModal = ({ isOpen, countdown, onConfirm }) => {
  if (!isOpen) return null

  const progressPercent = (countdown / 20) * 100

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 z-[9999] flex items-center justify-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8 max-w-md w-full mx-4 border-2 border-red-500 dark:border-red-700">
        <div className="flex items-start gap-3 mb-4">
          <span className="material-icons text-red-500 dark:text-red-400 text-3xl flex-shrink-0">error</span>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sessão Expirada</h2>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Sua sessão expirou por inatividade. Você será redirecionado para a tela de login em{' '}
          <strong className="text-red-500 dark:text-red-400">{countdown} segundos</strong>.
        </p>

        <div className="mb-6">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-1000"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        <button
          onClick={onConfirm}
          className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
        >
          <span className="material-icons sm">login</span>
          Ir para Login Agora
        </button>
      </div>
    </div>
  )
}

// ============ Release Notes ============
const NOTE_TYPES = {
  feature: {
    label: 'Novidade',
    icon: 'auto_awesome',
    badgeClass: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800/40'
  },
  fix: {
    label: 'Correção',
    icon: 'bug_report',
    badgeClass: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-800/40'
  },
  document: {
    label: 'Documentação',
    icon: 'description',
    badgeClass: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-100 dark:border-purple-800/40'
  }
}
const NOTE_TYPE_ORDER = Object.keys(NOTE_TYPES)

const CURRENT_VERSION = '1.3.0'
const CURRENT_VERSION_DATE = '25/10/2026'

// All entries belong to CURRENT_VERSION - the version is shown once in the
// panel header, not repeated per item.
const RELEASE_NOTES = [
  {
    type: 'feature',
    title: 'Novo conector Arya',
    text: 'Os eventos de resultado que vêm da central Logicmed agora também são encaminhados para o middleware, que realiza o despacho para o Arya.'
  },
  {
    type: 'feature',
    title: 'Novos endpoints de documentos do paciente',
    text: 'A API de clientes ganhou novos endpoints de documentos do paciente. Os documentos passam a ser armazenados no storage da plataforma e podem ser obtidos pelo cliente diretamente via API.'
  }
]

// ============ Top Status Bar ============
const TopStatusBar = ({ companyName, userName, apiToken, companyId, isCore, associatedCount, associatedIds, coreName, isDarkMode, toggleDarkMode }) => {
  const [isTokenDropdownOpen, setIsTokenDropdownOpen] = useState(false)
  const [tokenCopied, setTokenCopied] = useState(false)
  const [idCopied, setIdCopied] = useState(false)
  const [isQrModalOpen, setIsQrModalOpen] = useState(false)
  const [tokenIntegrations, setTokenIntegrations] = useState(null)
  // Companies embedded in the token (only present when the company is a núcleo): { ids, companies }
  const [tokenAssociated, setTokenAssociated] = useState(null)
  const [isLoadingTokenIntegrations, setIsLoadingTokenIntegrations] = useState(false)
  const [showReleaseNotes, setShowReleaseNotes] = useState(false)
  const [expandedNotes, setExpandedNotes] = useState(() => new Set())
  const [noteFilter, setNoteFilter] = useState('all')

  const toggleReleaseNote = (index) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  // Keeps each note's original index (used for expand/collapse + default
  // state) stable regardless of filtering/sorting below.
  const notesWithIndex = RELEASE_NOTES.map((note, index) => ({ ...note, index }))
  const visibleNotes = noteFilter === 'all'
    ? [...notesWithIndex].sort((a, b) => NOTE_TYPE_ORDER.indexOf(a.type) - NOTE_TYPE_ORDER.indexOf(b.type))
    : notesWithIndex.filter((note) => note.type === noteFilter)

  useEffect(() => {
    if (!showReleaseNotes) return
    // Every note starts collapsed each time the panel is opened.
    setExpandedNotes(new Set())
    const onKeyDown = (e) => { if (e.key === 'Escape') setShowReleaseNotes(false) }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [showReleaseNotes])

  useEffect(() => {
    if (!isTokenDropdownOpen || !apiToken) return

    let cancelled = false
    setIsLoadingTokenIntegrations(true)
    api.post('/api-tokens/validate', { token: apiToken })
      .then((response) => {
        if (cancelled) return
        setTokenIntegrations(response.data?.valid ? (response.data.integration_names || []) : [])
        setTokenAssociated(response.data?.valid
          ? { ids: response.data.associated_company_ids || [], companies: response.data.associated_companies || [] }
          : null)
      })
      .catch(() => {
        if (!cancelled) {
          setTokenIntegrations([])
          setTokenAssociated(null)
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTokenIntegrations(false)
      })

    return () => { cancelled = true }
  }, [isTokenDropdownOpen, apiToken])

  const handleCopyId = () => {
    if (companyId) {
      navigator.clipboard.writeText(companyId).then(() => {
        setIdCopied(true)
        setTimeout(() => setIdCopied(false), 2000)
        toast.success('ID da empresa copiado')
      }).catch(() => {
        toast.error('Erro ao copiar ID')
      })
    }
  }

  const handleCopyToken = () => {
    if (apiToken) {
      navigator.clipboard.writeText(apiToken).then(() => {
        setTokenCopied(true)
        setTimeout(() => setTokenCopied(false), 2000)
        toast.success('Token copiado para área de transferência')
      }).catch(() => {
        toast.error('Erro ao copiar token')
      })
    }
  }

  // Derived colours based on mode
  const accent      = isDarkMode ? '#25C6A4' : '#FFFFFF'
  const textPrimary = '#FFFFFF'
  const textMuted   = isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.75)'
  const textFaint   = isDarkMode ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.6)'
  const dividerCol  = isDarkMode ? 'rgba(37,198,164,0.25)' : 'rgba(255,255,255,0.3)'
  // Núcleo chip (purple), same shape as the version chip on the right: translucent on the dark bar,
  // near-solid white on the teal bar for contrast
  const coreChip = isDarkMode
    ? { bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.35)', fg: '#C4B5FD' }
    : { bg: 'rgba(255,255,255,0.92)', border: 'rgba(255,255,255,0.92)', fg: '#7E22CE' }
  const coreChipStyle = { backgroundColor: coreChip.bg, border: `1px solid ${coreChip.border}` }

  // Token dropdown palette: the panel follows the theme (it used to be hard-coded dark)
  const tk = isDarkMode
    ? {
        panelBg: '#0A1C28', panelBorder: 'rgba(37,198,164,0.25)', panelShadow: '0 8px 32px rgba(0,0,0,0.4)',
        title: '#FFFFFF', close: 'rgba(255,255,255,0.4)', closeHover: '#FFFFFF',
        boxBg: 'rgba(255,255,255,0.05)', boxBorder: 'rgba(37,198,164,0.15)',
        label: 'rgba(255,255,255,0.5)', text: 'rgba(255,255,255,0.75)', inputText: 'rgba(255,255,255,0.7)', strong: '#FFFFFF',
        accent: '#25C6A4',
        copyBg: 'rgba(37,198,164,0.1)', copyBgHover: 'rgba(37,198,164,0.2)', copyBorder: 'rgba(37,198,164,0.2)', copyBorderDone: 'rgba(37,198,164,0.3)',
        purple: '#C4B5FD', purpleBg: 'rgba(168,85,247,0.08)', purpleBorder: 'rgba(168,85,247,0.25)',
        warn: 'rgba(251,191,36,0.85)'
      }
    : {
        panelBg: '#FFFFFF', panelBorder: '#E5E7EB', panelShadow: '0 8px 32px rgba(0,0,0,0.15)',
        title: '#111827', close: '#9CA3AF', closeHover: '#374151',
        boxBg: '#F9FAFB', boxBorder: '#E5E7EB',
        label: '#6B7280', text: '#374151', inputText: '#4B5563', strong: '#111827',
        accent: '#0E9F84',
        copyBg: 'rgba(37,198,164,0.12)', copyBgHover: 'rgba(37,198,164,0.22)', copyBorder: 'rgba(14,159,132,0.35)', copyBorderDone: 'rgba(14,159,132,0.5)',
        purple: '#7E22CE', purpleBg: 'rgba(168,85,247,0.07)', purpleBorder: 'rgba(168,85,247,0.3)',
        warn: '#B45309'
      }

  return (
    <>
    <div
      className="h-10 flex items-center justify-between px-6 gap-6"
      style={{
        backgroundColor: isDarkMode ? '#0A1C28' : '#25C6A4',
        borderBottom: isDarkMode ? '1px solid rgba(37,198,164,0.2)' : '1px solid rgba(0,0,0,0.08)',
        boxShadow: isDarkMode ? '0 2px 12px rgba(37,198,164,0.15)' : '0 2px 12px rgba(37,198,164,0.3)'
      }}
    >
      {/* Left Side */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="material-icons" style={{ fontSize: '14px', color: accent }}>domain</span>
          <span className="font-medium" style={{ color: textPrimary }}>{companyName}</span>
        </div>

        <div className="h-4 w-px" style={{ backgroundColor: dividerCol }}></div>

        <div className="flex items-center gap-1.5 text-sm">
          {isCore && (
            <span
              title={`${associatedCount} empresa${associatedCount !== 1 ? 's' : ''} associada${associatedCount !== 1 ? 's' : ''}`}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full whitespace-nowrap"
              style={coreChipStyle}
            >
              <span className="material-icons" style={{ fontSize: '14px', color: coreChip.fg }}>hub</span>
              <span className="w-px h-3" style={{ backgroundColor: coreChip.border }} />
              <span className="text-xs font-mono font-medium tracking-wide uppercase" style={{ color: coreChip.fg }}>Núcleo</span>
            </span>
          )}
          {!isCore && coreName && (
            <span
              title={`Esta empresa faz parte do núcleo ${coreName}`}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full whitespace-nowrap max-w-[240px]"
              style={coreChipStyle}
            >
              <span className="material-icons flex-shrink-0" style={{ fontSize: '14px', color: coreChip.fg }}>account_tree</span>
              <span className="w-px h-3 flex-shrink-0" style={{ backgroundColor: coreChip.border }} />
              <span className="text-xs font-mono font-medium tracking-wide truncate" style={{ color: coreChip.fg }}>Núcleo: {coreName}</span>
            </span>
          )}
          <span className="material-icons" style={{ fontSize: '13px', color: textFaint }}>fingerprint</span>
          <span className="font-mono text-xs tracking-tight" style={{ color: textMuted }}>
            {companyId || '—'}
          </span>
          <button
            onClick={handleCopyId}
            title="Copiar ID da empresa"
            className="p-0.5 rounded flex items-center justify-center transition"
            style={{ color: idCopied ? accent : textFaint }}
            onMouseEnter={e => e.currentTarget.style.color = accent}
            onMouseLeave={e => e.currentTarget.style.color = idCopied ? accent : textFaint}
          >
            <span className="material-icons" style={{ fontSize: '13px' }}>{idCopied ? 'check' : 'content_copy'}</span>
          </button>
        </div>

        <div className="h-4 w-px" style={{ backgroundColor: dividerCol }}></div>

        <div className="flex items-center gap-2 text-sm">
          <span className="material-icons" style={{ fontSize: '14px', color: textFaint }}>person</span>
          <span style={{ color: textMuted }}>{userName}</span>
          <div className="relative ml-1 flex items-center">
            <button
              onClick={() => setIsTokenDropdownOpen(!isTokenDropdownOpen)}
              title="Ver token de autenticação"
              className="p-1 rounded flex items-center justify-center transition"
              style={{ color: isTokenDropdownOpen ? accent : textFaint }}
              onMouseEnter={e => e.currentTarget.style.color = accent}
              onMouseLeave={e => e.currentTarget.style.color = isTokenDropdownOpen ? accent : textFaint}
            >
              <span className="material-icons" style={{ fontSize: '16px' }}>vpn_key</span>
            </button>
            {isTokenDropdownOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 rounded-lg shadow-xl p-4 z-50"
                style={{
                  backgroundColor: tk.panelBg,
                  border: `1px solid ${tk.panelBorder}`,
                  boxShadow: tk.panelShadow
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold" style={{ color: tk.title }}>Token de Autenticação (APIKEY)</h3>
                  <button
                    onClick={() => setIsTokenDropdownOpen(false)}
                    style={{ color: tk.close }}
                    onMouseEnter={e => e.currentTarget.style.color = tk.closeHover}
                    onMouseLeave={e => e.currentTarget.style.color = tk.close}
                  >
                    <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
                  </button>
                </div>

                <div className="rounded p-3 mb-3" style={{ backgroundColor: tk.boxBg, border: `1px solid ${tk.boxBorder}` }}>
                  <p className="text-xs font-medium mb-2" style={{ color: tk.label }}>Integrações Associadas</p>
                  {isLoadingTokenIntegrations ? (
                    <p className="text-xs" style={{ color: tk.label }}>Carregando...</p>
                  ) : tokenIntegrations && tokenIntegrations.length > 0 ? (
                    <ul className="space-y-1">
                      {tokenIntegrations.map((name, idx) => (
                        <li key={idx} className="flex items-center gap-1.5 text-xs" style={{ color: tk.text }}>
                          <span className="material-icons" style={{ fontSize: '12px', color: tk.accent }}>check</span>
                          {name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs" style={{ color: tk.label }}>Nenhuma integração associada</p>
                  )}
                </div>

                {isCore && (() => {
                  const tokenIds = tokenAssociated?.ids || []
                  // The token is a snapshot: flag it when the núcleo's current associations differ from what it carries
                  const isOutdated = !isLoadingTokenIntegrations && tokenAssociated !== null &&
                    (tokenIds.length !== (associatedIds || []).length || tokenIds.some(id => !(associatedIds || []).includes(id)))
                  return (
                    <div className="rounded p-3 mb-3" style={{ backgroundColor: tk.purpleBg, border: `1px solid ${tk.purpleBorder}` }}>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: tk.purple }}>
                        <span className="material-icons" style={{ fontSize: '13px' }}>hub</span>
                        Empresas Associadas (Núcleo)
                      </p>
                      {isLoadingTokenIntegrations ? (
                        <p className="text-xs" style={{ color: tk.label }}>Carregando...</p>
                      ) : tokenIds.length > 0 ? (
                        <ul className="space-y-1 max-h-40 overflow-y-auto">
                          {tokenIds.map((id) => {
                            const assoc = (tokenAssociated?.companies || []).find(c => c.id === id)
                            return (
                              <li key={id} className="flex items-center gap-1.5 text-xs" style={{ color: tk.text }} title={id}>
                                <span className="material-icons flex-shrink-0" style={{ fontSize: '12px', color: tk.purple }}>apartment</span>
                                <span className="truncate">{assoc ? (assoc.nome_fantasia || assoc.razao_social) : 'Empresa não encontrada'}</span>
                              </li>
                            )
                          })}
                        </ul>
                      ) : (
                        <p className="text-xs" style={{ color: tk.label }}>Nenhuma empresa associada neste token</p>
                      )}
                      {isOutdated && (
                        <p className="text-[11px] mt-2 leading-snug" style={{ color: tk.warn }}>
                          As empresas associadas mudaram desde a emissão deste token. Solicite um novo token ao administrador.
                        </p>
                      )}
                    </div>
                  )
                })()}

                {!isCore && coreName && (
                  <div className="rounded p-3 mb-3" style={{ backgroundColor: tk.purpleBg, border: `1px solid ${tk.purpleBorder}` }}>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1.5" style={{ color: tk.purple }}>
                      <span className="material-icons" style={{ fontSize: '13px' }}>account_tree</span>
                      Núcleo
                    </p>
                    <p className="text-xs leading-snug" style={{ color: tk.text }}>
                      Esta empresa faz parte do núcleo <strong style={{ color: tk.strong }}>{coreName}</strong>
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={apiToken || 'Carregando token...'}
                    title={apiToken || ''}
                    className="flex-1 min-w-0 truncate px-2.5 py-2 rounded text-xs font-mono"
                    style={{ backgroundColor: tk.boxBg, border: `1px solid ${tk.boxBorder}`, color: tk.inputText }}
                  />
                  <button
                    onClick={handleCopyToken}
                    title={tokenCopied ? 'Copiado!' : 'Copiar Token'}
                    className="flex-shrink-0 px-3 py-2 rounded text-sm font-medium transition flex items-center justify-center gap-1.5"
                    style={tokenCopied
                      ? { backgroundColor: tk.copyBgHover, color: tk.accent, border: `1px solid ${tk.copyBorderDone}` }
                      : { backgroundColor: tk.copyBg, color: tk.accent, border: `1px solid ${tk.copyBorder}` }
                    }
                    onMouseEnter={e => { if (!tokenCopied) e.currentTarget.style.backgroundColor = tk.copyBgHover }}
                    onMouseLeave={e => { if (!tokenCopied) e.currentTarget.style.backgroundColor = tk.copyBg }}
                  >
                    <span className="material-icons" style={{ fontSize: '16px' }}>{tokenCopied ? 'check' : 'content_copy'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsQrModalOpen(true)}
            title="Login pelo celular (QR Code)"
            className="p-1 rounded flex items-center justify-center transition"
            style={{ color: textFaint }}
            onMouseEnter={e => e.currentTarget.style.color = accent}
            onMouseLeave={e => e.currentTarget.style.color = textFaint}
          >
            <span className="material-icons" style={{ fontSize: '16px' }}>phonelink</span>
          </button>
        </div>
      </div>

      <QRLoginModal
        isOpen={isQrModalOpen}
        onClose={() => setIsQrModalOpen(false)}
        companyId={companyId}
        username={userName}
        token={apiToken}
      />

      {/* Right Side */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowReleaseNotes(true)}
          title="Ver notas da versão"
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-full transition"
          style={{
            backgroundColor: isDarkMode ? 'rgba(37,198,164,0.1)' : 'rgba(255,255,255,0.12)',
            border: `1px solid ${dividerCol}`
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(37,198,164,0.2)' : 'rgba(255,255,255,0.22)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(37,198,164,0.1)' : 'rgba(255,255,255,0.12)'}
        >
          <span className="material-icons" style={{ fontSize: '14px', color: textMuted }}>local_offer</span>
          <span className="w-px h-3" style={{ backgroundColor: dividerCol }} />
          <span className="text-xs font-mono font-medium tracking-wide" style={{ color: textMuted }}>v{CURRENT_VERSION}</span>
        </button>
        <div className="h-4 w-px" style={{ backgroundColor: dividerCol }} />
        <button
          onClick={toggleDarkMode}
          className="p-1 rounded flex items-center justify-center transition"
          style={{ color: textFaint }}
          title={isDarkMode ? 'Mudar para modo claro' : 'Mudar para modo escuro'}
          onMouseEnter={e => e.currentTarget.style.color = accent}
          onMouseLeave={e => e.currentTarget.style.color = textFaint}
        >
          <span className="material-icons" style={{ fontSize: '16px' }}>
            {isDarkMode ? 'light_mode' : 'dark_mode'}
          </span>
        </button>
      </div>
    </div>

    {/* Release notes panel */}
    {showReleaseNotes && (
      <>
        <div
          className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[9998]"
          onClick={() => setShowReleaseNotes(false)}
        />
        <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white dark:bg-gray-800 shadow-2xl z-[9999] flex flex-col">
          <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
              <span className="material-icons text-emerald-600 dark:text-emerald-400" style={{ fontSize: '18px' }}>local_offer</span>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Notas da Versão</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
                Versão atual
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800/40">
                  v{CURRENT_VERSION}
                </span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300">
                  <span className="material-icons" style={{ fontSize: '11px' }}>calendar_today</span>
                  {CURRENT_VERSION_DATE}
                </span>
              </p>
            </div>
            <button
              onClick={() => setShowReleaseNotes(false)}
              title="Fechar"
              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition"
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={() => setNoteFilter('all')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                noteFilter === 'all'
                  ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 border-gray-800 dark:border-gray-200'
                  : 'bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <span className="material-icons" style={{ fontSize: '12px' }}>apps</span>
              Todos
            </button>
            {NOTE_TYPE_ORDER.map((typeKey) => {
              const cfg = NOTE_TYPES[typeKey]
              const isActive = noteFilter === typeKey
              return (
                <button
                  key={typeKey}
                  onClick={() => setNoteFilter(typeKey)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                    isActive
                      ? cfg.badgeClass
                      : 'bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons" style={{ fontSize: '12px' }}>{cfg.icon}</span>
                  {cfg.label}
                </button>
              )
            })}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {visibleNotes.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-6">
                Nenhuma nota do tipo "{NOTE_TYPES[noteFilter]?.label}" nesta versão
              </p>
            ) : (
              visibleNotes.map((note) => {
                const isExpanded = expandedNotes.has(note.index)
                const typeCfg = NOTE_TYPES[note.type] || NOTE_TYPES.feature
                return (
                  <div
                    key={note.index}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleReleaseNote(note.index)}
                      className="w-full flex items-center justify-between gap-2 px-3.5 py-3 text-left bg-gray-50/80 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${typeCfg.badgeClass}`}>
                          <span className="material-icons" style={{ fontSize: '12px' }}>{typeCfg.icon}</span>
                          {typeCfg.label}
                        </span>
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{note.title}</span>
                      </div>
                      <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{ fontSize: '18px' }}>
                        {isExpanded ? 'expand_less' : 'expand_more'}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="px-3.5 py-3 bg-white dark:bg-gray-800">
                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{note.text}</p>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      </>
    )}
    </>
  )
}

// ============ Sidebar Service Status ============
const STATUS_CFG = {
  healthy:      { label: 'Online',      dot: 'bg-green-500',  text: 'text-green-600 dark:text-green-400' },
  unhealthy:    { label: 'Offline',     dot: 'bg-red-500',    text: 'text-red-600 dark:text-red-400' },
  connected:    { label: 'Online',      dot: 'bg-green-500',  text: 'text-green-600 dark:text-green-400' },
  disconnected: { label: 'Offline',     dot: 'bg-red-500',    text: 'text-red-600 dark:text-red-400' },
  checking:     { label: 'Verificando…',dot: 'bg-yellow-400', text: 'text-yellow-600 dark:text-yellow-400' },
  success:      { label: 'Online',      dot: 'bg-green-500',  text: 'text-green-600 dark:text-green-400' },
  warning:      { label: 'Atenção',     dot: 'bg-yellow-500', text: 'text-yellow-600 dark:text-yellow-400' },
  error:        { label: 'Offline',     dot: 'bg-red-500',    text: 'text-red-600 dark:text-red-400' },
}

const StatusDot = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.checking
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  )
}

const UPTIME_URL = import.meta.env.VITE_UPTIME_URL

const SidebarServiceStatus = () => {
  const [apiStatus,         setApiStatus]         = useState('checking')
  const [dbStatus,          setDbStatus]          = useState('checking')
  const [middlewareStatus,  setMiddlewareStatus]  = useState('checking')
  const [middlewareDetails, setMiddlewareDetails] = useState(null)
  const [middlewareOpen,    setMiddlewareOpen]    = useState(false)
  const [middlewareRefreshing, setMiddlewareRefreshing] = useState(false)
  const [loading,           setLoading]           = useState(false)

  const check = useCallback(async () => {
    setLoading(true)
    setApiStatus('checking')
    setDbStatus('checking')
    setMiddlewareStatus('checking')
    try {
      await api.get('/health', { silentLoading: true })
      setApiStatus('healthy')
    } catch {
      setApiStatus('unhealthy')
    }
    try {
      const res = await api.get('/setup/info', { silentLoading: true })
      setDbStatus(res.data.database_connected ? 'connected' : 'disconnected')
      setMiddlewareStatus(res.data.middleware?.status || 'error')
      setMiddlewareDetails(res.data.middleware || null)
    } catch {
      setDbStatus('disconnected')
      setMiddlewareStatus('error')
      setMiddlewareDetails(null)
    }
    setLoading(false)
  }, [])

  const refreshMiddleware = useCallback(async () => {
    setMiddlewareRefreshing(true)
    try {
      const res = await api.get('/setup/info', { silentLoading: true })
      setMiddlewareStatus(res.data.middleware?.status || 'error')
      setMiddlewareDetails(res.data.middleware || null)
    } catch {
      setMiddlewareStatus('error')
      setMiddlewareDetails(null)
    }
    setMiddlewareRefreshing(false)
  }, [])

  // Check once on mount — no polling
  useEffect(() => { check() }, [check])

  const SkeletonRow = () => (
    <div className="flex items-center gap-2 animate-pulse">
      <span className="w-3 h-3 rounded bg-gray-200 dark:bg-gray-700 shrink-0" />
      <span className="w-10 h-2.5 rounded bg-gray-200 dark:bg-gray-700" />
      <span className="w-16 h-2.5 rounded bg-gray-200 dark:bg-gray-700" />
    </div>
  )

  return (
    <div>
      <div className="h-px bg-gray-200 dark:bg-gray-700 mb-3" />
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          {loading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : (
            <div className="grid grid-cols-[auto_auto_auto] items-center justify-items-start gap-x-2 gap-y-2">
              <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: 13 }}>cloud</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">API</span>
              <StatusDot status={apiStatus} />

              <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: 13 }}>storage</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Banco</span>
              <StatusDot status={dbStatus} />

              <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: 13 }}>hub</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Middleware</span>
              <span className="flex items-center gap-1.5">
                <StatusDot status={middlewareStatus} />
                <button
                  type="button"
                  onClick={() => setMiddlewareOpen(true)}
                  title="Ver detalhes do middleware"
                  className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                >
                  <span className="material-icons leading-none" style={{ fontSize: 13 }}>north_east</span>
                </button>
              </span>
            </div>
          )}
        </div>
        <button
          onClick={check}
          disabled={loading}
          title="Atualizar status"
          className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-40"
        >
          <span className={`material-icons ${loading ? 'animate-spin' : ''}`} style={{ fontSize: 15 }}>refresh</span>
        </button>
      </div>
      {UPTIME_URL && (
        <a
          href={UPTIME_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir página de status em uma nova aba"
          className="mt-3 flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg text-xs font-medium border transition-colors"
          style={{
            color: '#25C6A4',
            borderColor: 'rgba(37,198,164,0.35)',
            backgroundColor: 'rgba(37,198,164,0.06)',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(37,198,164,0.14)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(37,198,164,0.06)'}
        >
          <span className="material-icons" style={{ fontSize: 14 }}>monitor_heart</span>
          Saúde dos Serviços
          <span className="material-icons" style={{ fontSize: 13 }}>open_in_new</span>
        </a>
      )}
      <MiddlewareStatusModal
        isOpen={middlewareOpen}
        onClose={() => setMiddlewareOpen(false)}
        status={middlewareDetails?.status || middlewareStatus}
        services={middlewareDetails?.services || []}
        error={middlewareDetails?.error}
        onRefresh={refreshMiddleware}
        refreshing={middlewareRefreshing}
      />
    </div>
  )
}

const VALID_COMPANY_PAGES = ['home', 'integrations', 'users', 'settings', 'configs']

// ============ Main Dashboard Component ============
const CompanyDashboardPage = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const getPageFromUrl = () => {
    const sub = location.pathname.replace(/^\/dashboard\/?/, '')
    return VALID_COMPANY_PAGES.includes(sub) ? sub : 'home'
  }

  const [currentPage, setCurrentPage] = useState(getPageFromUrl)
  const [userInfo, setUserInfo] = useState(null)
  const [companyInfo, setCompanyInfo] = useState(null)
  const [integrations, setIntegrations] = useState([])
  const [selectedIntegration, setSelectedIntegration] = useState(null)
  const [isRenewing, setIsRenewing] = useState(false)
  const [showExpirationModal, setShowExpirationModal] = useState(false)
  const [showExpiredModal, setShowExpiredModal] = useState(false)
  const [expiredCountdown, setExpiredCountdown] = useState(20)
  const [apiToken, setApiToken] = useState('')
  const [parametersStatus, setParametersStatus] = useState({})
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('darkMode') !== 'false'
  )
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem('company_sidebar_collapsed') === 'true'
  )

  // Modal states
  const [showEventsModal, setShowEventsModal] = useState(false)
  const [showParametersModal, setShowParametersModal] = useState(false)
  const [showDocumentationModal, setShowDocumentationModal] = useState(false)
  const [selectedIntegrationForModal, setSelectedIntegrationForModal] = useState(null)

  // Núcleo: tab selected on the home page (null = the logged company), and the company whose traces/errors
  // page is open (null = the logged company). They differ because the traces page can also be opened from
  // the logged company's own Integrations page while an associated company's tab is selected on the home.
  const [activeCompanyId, setActiveCompanyId] = useState(null)
  const [drillCompanyId, setDrillCompanyId] = useState(null)

  // Traces page state
  const [selectedIntegrationForTraces, setSelectedIntegrationForTraces] = useState(null)
  const [selectedPeriodForTraces, setSelectedPeriodForTraces] = useState(null)

  // Errors page state - reuses the stats already fetched by the dashboard's
  // IntegrationsOverview/CompanyHomePage instead of re-fetching them
  const [errorsPageStats, setErrorsPageStats] = useState([])
  const [errorsPagePeriod, setErrorsPagePeriod] = useState(null)

  // Login-time franchise usage warning (events quota) - null while not shown.
  const [franchiseAlertUsage, setFranchiseAlertUsage] = useState(null)

  const [token, setToken] = useState(localStorage.getItem('access_token'))

  // Apply dark mode class to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('darkMode', isDarkMode)
  }, [isDarkMode])

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
  }

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('company_sidebar_collapsed', next)
      return next
    })
  }

  const handleSessionExpire = useCallback(() => {
    setShowExpiredModal(true)
  }, [])

  // Token expiration management
  const { timeRemaining, isExpiring } = useTokenExpiration(token, handleSessionExpire)

  useEffect(() => {
    if (isExpiring && timeRemaining && timeRemaining > 0) {
      setShowExpirationModal(true)
    }
  }, [isExpiring, timeRemaining])

  // Sync currentPage when URL changes (browser back/forward)
  useEffect(() => {
    const page = getPageFromUrl()
    if (page !== currentPage) setCurrentPage(page)
  }, [location.pathname])

  const navigateTo = (page) => {
    setCurrentPage(page)
    navigate(`/dashboard/${page}`)
  }

  // Load user and company info
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token')
    const userInfoStr = localStorage.getItem('user_info')

    if (!storedToken || !userInfoStr) {
      navigate('/login')
      return
    }

    try {
      const info = JSON.parse(userInfoStr)
      setUserInfo(info)
      loadCompanyData(info.company_id)
      loadApiToken(info.company_id)
      loadParametersStatus(info.company_id)
    } catch (error) {
      console.error('Error parsing user_info:', error)
      navigate('/login')
    }
  }, [])

  // Login-time franchise usage warning - runs once per login (session), as
  // soon as the company's API key (needed to call the franchise endpoint) is
  // available. Skipped entirely if the user already clicked "Estou ciente"
  // for today (see isFranchiseAlertDismissedToday).
  const franchiseAlertCheckedRef = useRef(false)
  useEffect(() => {
    if (!apiToken || !userInfo?.company_id || franchiseAlertCheckedRef.current) return
    franchiseAlertCheckedRef.current = true

    if (isFranchiseAlertDismissedToday(userInfo.company_id)) return

    franchiseService.getUsage(apiToken)
      .then((data) => {
        const events = data?.events
        if (!events) return
        if (events.percentage_used >= events.alert_threshold_percentage) {
          setFranchiseAlertUsage({
            percentage: events.percentage_used,
            used: events.used,
            limit: events.limit
          })
        }
      })
      .catch((error) => {
        console.error('Error checking franchise usage for alert:', error)
      })
  }, [apiToken, userInfo])

  // Monitor user status
  useEffect(() => {
    if (!userInfo?.id) return

    const checkStatus = async () => {
      try {
        const response = await api.get(`/company-users/check-status/${userInfo.id}`, { silentLoading: true })
        const data = response.data
        if (!data.is_active) {
          toast.error('Sua conta foi desativada. Você será desconectado.')
          setTimeout(handleLogout, 2000)
        }
      } catch (error) {
        console.error('Error checking status:', error)
      }
    }

    checkStatus()
    const interval = setInterval(checkStatus, 15000)

    return () => clearInterval(interval)
  }, [userInfo])

  // Session expired countdown
  useEffect(() => {
    if (!showExpiredModal) return

    const interval = setInterval(() => {
      setExpiredCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          handleLogout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [showExpiredModal])

  const loadCompanyData = async (companyId) => {
    try {
      const response = await api.get(`/companies/search/id/${companyId}`)
      const data = response.data
      setCompanyInfo(data)
      setIntegrations(data.integrations || [])
    } catch (error) {
      console.error('Error loading company data:', error)
    }
  }

  const loadApiToken = async (companyId) => {
    try {
      const response = await api.get(`/api-tokens/company/${companyId}`)
      const data = response.data
      // API returns { items: [...], total, page, page_size }
      // Get the first token from the list
      if (data.items && data.items.length > 0) {
        setApiToken(data.items[0].token || '')
      } else {
        setApiToken('')
      }
    } catch (error) {
      console.error('Error loading API token:', error)
      setApiToken('')
    }
  }

  const loadParametersStatus = async (companyId) => {
    try {
      const response = await api.get(`/company/integrations/parameters-status?company_id=${companyId}`)
      const data = response.data
      // Backend returns a dict: { "integration_id": { has_parameters, ... }, ... }
      const statusMap = {}
      Object.entries(data).forEach(([integration_id, status]) => {
        statusMap[integration_id] = {
          has_parameters: status.has_parameters,
          required_parameters: status.required_parameters,
          filled_parameters: status.filled_required,
          all_required_filled: status.all_required_filled,
        }
      })
      setParametersStatus(statusMap)
    } catch (error) {
      console.error('Error loading parameters status:', error)
      setParametersStatus({})
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user_info')
    navigate('/login')
  }

  const handleRenewSession = async () => {
    setIsRenewing(true)
    try {
      const response = await api.post('/auth/refresh', {})
      const data = response.data
      localStorage.setItem('access_token', data.access_token)
      setToken(data.access_token)
      toast.success('Sessão renovada com sucesso!')
      setShowExpirationModal(false)
    } catch (error) {
      toast.error('Erro ao renovar sessão')
    } finally {
      setIsRenewing(false)
    }
  }

  // targetCompanyId: an associated company (núcleo only) whose data the page shows; null = the logged company
  const handleOpenTracesPage = (integration, period, targetCompanyId = null) => {
    setSelectedIntegrationForTraces(integration)
    setSelectedPeriodForTraces(period || null)
    setDrillCompanyId(targetCompanyId)
    setCurrentPage('traces')
  }

  const handleOpenErrorsPage = (statsSnapshot, period, targetCompanyId = null) => {
    setErrorsPageStats(statsSnapshot || [])
    setErrorsPagePeriod(period || null)
    setDrillCompanyId(targetCompanyId)
    setCurrentPage('errors')
  }


  const handleOpenEventsModal = (integration) => {
    setSelectedIntegrationForModal(integration)
    setShowEventsModal(true)
  }

  const handleOpenParamsModal = (integration) => {
    setSelectedIntegrationForModal(integration)
    setShowParametersModal(true)
  }


  const handleCloseEventsModal = () => {
    setShowEventsModal(false)
    setSelectedIntegrationForModal(null)
  }

  const handleOpenDocumentationModal = (integration) => {
    setSelectedIntegrationForModal(integration)
    setShowDocumentationModal(true)
  }

  const handleCloseDocumentationModal = () => {
    setShowDocumentationModal(false)
    setSelectedIntegrationForModal(null)
  }

  const handleCloseParamsModal = () => {
    setShowParametersModal(false)
    setSelectedIntegrationForModal(null)
    // Reload parameters status after closing params modal
    if (userInfo?.company_id) {
      loadParametersStatus(userInfo.company_id)
    }
  }

  const urlSubPath = location.pathname.replace(/^\/dashboard\/?/, '')
  if (urlSubPath && !VALID_COMPANY_PAGES.includes(urlSubPath)) {
    return <NotFoundPage />
  }

  // Núcleo: the associated companies this user can switch to (empty for any other company)
  const associatedCompanies = companyInfo?.is_core ? (companyInfo.associated_companies || []) : []
  const viewedHomeCompany = associatedCompanies.find((c) => c.id === activeCompanyId) || null
  const drillCompany = associatedCompanies.find((c) => c.id === drillCompanyId) || null

  if (!userInfo || !companyInfo) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <span className="material-icons text-4xl text-blue-500 animate-spin">refresh</span>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Carregando dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-950">
      {/* Top Status Bar */}
      <TopStatusBar
        companyName={companyInfo.razao_social}
        userName={userInfo.username}
        apiToken={apiToken}
        companyId={userInfo.company_id}
        isCore={!!companyInfo.is_core}
        associatedCount={companyInfo.associated_companies?.length || 0}
        associatedIds={(companyInfo.associated_companies || []).map(c => c.id)}
        coreName={companyInfo.core ? (companyInfo.core.nome_fantasia || companyInfo.core.razao_social) : ''}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Fixed Sidebar */}
      <div
        className={`fixed left-0 top-10 bottom-0 ${sidebarCollapsed ? 'w-20' : 'w-72'} bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col z-40 overflow-hidden transition-[width] duration-300 ease-in-out`}
      >
        {/* Collapse toggle */}
        <button
          onClick={toggleSidebarCollapsed}
          title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition z-50"
        >
          <span className="material-icons" style={{ fontSize: '18px' }}>menu</span>
        </button>

        {/* Sidebar Header */}
        <div className={`flex-shrink-0 border-b border-gray-200 dark:border-gray-700 transition-all duration-300 ${sidebarCollapsed ? 'px-2 pt-11 pb-6 short:pb-3' : 'px-4 py-6 short:py-3'}`}>
          <div className={`flex items-center mb-3 short:mb-2 ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
              <img
                src={logo}
                alt="SF"
                className={`object-contain rounded-full transition-all duration-300 ${sidebarCollapsed ? 'w-9 h-9' : 'w-14 h-14'}`}
              />
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-md" style={{ fontFamily: 'Blare, sans-serif' }}>
                  <span className="text-gray-900 dark:text-white">Solid</span><span style={{ color: '#25C6A4' }}>.</span><span className="text-gray-900 dark:text-white">Flow</span>
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: "'Stolzl', sans-serif" }}>Hub de Integrações</p>
              </div>
            )}
          </div>
          {!sidebarCollapsed && <SidebarServiceStatus />}
        </div>

        {/* Scrollable middle: navigation + franchise. Header and session stay pinned so
            the session card is never clipped on short viewports. */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Navigation */}
        <nav className={`space-y-1 transition-all duration-300 ${sidebarCollapsed ? 'px-2 py-5 short:py-2' : 'px-3 py-5 short:py-2'}`}>
          {!sidebarCollapsed && (
            <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Menu
            </p>
          )}
          <NavItem
            icon={<span className="material-icons" style={{ fontSize: '18px' }}>space_dashboard</span>}
            label="Dashboard"
            subtitle="Visão geral"
            active={currentPage === 'home'}
            onClick={() => navigateTo('home')}
            collapsed={sidebarCollapsed}
          />
          <NavItem
            icon={<span className="material-icons" style={{ fontSize: '18px' }}>bolt</span>}
            label="Integrações"
            subtitle="Conectores e eventos"
            active={currentPage === 'integrations'}
            onClick={() => navigateTo('integrations')}
            collapsed={sidebarCollapsed}
          />
          {userInfo.is_admin && (
            <NavItem
              icon={<span className="material-icons" style={{ fontSize: '18px' }}>people</span>}
              label="Usuários"
              subtitle="Gerenciar acessos"
              active={currentPage === 'users'}
              onClick={() => navigateTo('users')}
              collapsed={sidebarCollapsed}
            />
          )}
          <NavItem
            icon={<span className="material-icons" style={{ fontSize: '18px' }}>list_alt</span>}
            label="Cadastros"
            subtitle="Exames e especialidades"
            active={currentPage === 'settings'}
            onClick={() => navigateTo('settings')}
            collapsed={sidebarCollapsed}
          />
          <NavItem
            icon={<span className="material-icons" style={{ fontSize: '18px' }}>settings</span>}
            label="Configurações"
            subtitle="Variáveis do sistema"
            active={currentPage === 'configs'}
            onClick={() => navigateTo('configs')}
            collapsed={sidebarCollapsed}
          />
        </nav>

        {/* Franchise usage card */}
        {!sidebarCollapsed && (
          <div className="px-3 pb-2">
            <FranchiseUsageCard apiToken={apiToken} />
          </div>
        )}
        </div>

        {/* Session Card at bottom */}
        <div className={`flex-shrink-0 border-t border-gray-200 dark:border-gray-700 transition-all duration-300 ${sidebarCollapsed ? 'p-2' : 'p-4 short:p-3'}`}>
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/25 flex items-center justify-center"
                title={userInfo.username || userInfo.email || 'Usuário'}
              >
                <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '17px' }}>account_circle</span>
              </div>
              <button
                onClick={handleLogout}
                title="Sair"
                className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
              >
                <span className="material-icons" style={{ fontSize: '17px' }}>logout</span>
              </button>
            </div>
          ) : (
            <>
              <SessionCard
                token={token}
                userName={userInfo.username}
                userEmail={userInfo.email}
                isAdmin={userInfo.is_admin}
                onLogout={handleLogout}
                onRenew={handleRenewSession}
                isRenewing={isRenewing}
              />
            </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${sidebarCollapsed ? 'ml-28' : 'ml-80'} mr-2 mt-2 mb-6 transition-[margin] duration-300 ease-in-out`}>
        <div className={`w-full p-2 sm:p-3${currentPage === 'settings' ? ' flex flex-col flex-1 min-h-0' : ' overflow-y-auto flex-1'}`}>
          {currentPage === 'home' && (
            <CompanyHomePage
              companyId={userInfo.company_id}
              companyName={companyInfo.nome_fantasia || companyInfo.razao_social}
              associatedCompanies={associatedCompanies}
              activeCompanyId={viewedHomeCompany ? viewedHomeCompany.id : userInfo.company_id}
              onActiveCompanyChange={(id) => setActiveCompanyId(id === userInfo.company_id ? null : id)}
              integrations={integrations}
              onOpenTracesPage={(integration, period) => handleOpenTracesPage(integration, period, viewedHomeCompany?.id || null)}
              onOpenErrorsPage={(snapshot, period) => handleOpenErrorsPage(snapshot, period, viewedHomeCompany?.id || null)}
            />
          )}
        {currentPage === 'integrations' && (
          <CompanyIntegrationsPage
            integrations={integrations}
            userInfo={userInfo}
            parametersStatus={parametersStatus}
            onOpenTracesPage={handleOpenTracesPage}
            onOpenEventsModal={handleOpenEventsModal}
            onOpenParamsModal={handleOpenParamsModal}
            onOpenDocumentationModal={handleOpenDocumentationModal}
          />
        )}
        {currentPage === 'users' && userInfo.is_admin && <CompanyUsersPage companyId={userInfo.company_id} userInfo={userInfo} />}
        {currentPage === 'settings' && <CompanySettingsPage />}
        {currentPage === 'configs' && <CompanyConfigsPage />}
          {currentPage === 'traces' && selectedIntegrationForTraces && (
            <EventsTracePage
              integration={selectedIntegrationForTraces}
              initialPeriod={selectedPeriodForTraces}
              companyId={drillCompany ? drillCompany.id : userInfo.company_id}
              viewedCompany={drillCompany}
              onBack={() => setCurrentPage(drillCompany ? 'home' : 'integrations')}
              onOpenErrorsPage={(snapshot, period) => handleOpenErrorsPage(snapshot, period, drillCompany?.id || null)}
            />
          )}
          {currentPage === 'errors' && (
            <IntegrationsErrorsPage
              stats={errorsPageStats}
              period={errorsPagePeriod}
              integrations={drillCompany ? [] : integrations}
              companyId={drillCompany ? drillCompany.id : userInfo.company_id}
              viewedCompany={drillCompany}
              onBack={() => setCurrentPage('home')}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <SessionExpirationModal
        isOpen={showExpirationModal}
        timeRemaining={timeRemaining}
        onRenew={handleRenewSession}
        onLogout={handleLogout}
        loading={isRenewing}
      />

      <SessionExpiredModal isOpen={showExpiredModal} countdown={expiredCountdown} onConfirm={handleLogout} />

      {/* Integration Modals */}
      <EventsModal
        isOpen={showEventsModal}
        onClose={handleCloseEventsModal}
        integration={selectedIntegrationForModal}
      />

      <ParametersModal
        isOpen={showParametersModal}
        onClose={handleCloseParamsModal}
        integration={selectedIntegrationForModal}
        companyId={userInfo?.company_id}
      />

      <DocumentationModal
        isOpen={showDocumentationModal}
        onClose={handleCloseDocumentationModal}
        integration={selectedIntegrationForModal}
      />

      <ToastContainer />

      {franchiseAlertUsage && (
        <FranchiseUsageAlert
          companyId={userInfo?.company_id}
          usage={franchiseAlertUsage}
          onDismiss={() => setFranchiseAlertUsage(null)}
        />
      )}
    </div>
  )
}

export default CompanyDashboardPage
